/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class ScimTokensService {
  /**
   * @param idpName Identity provider name
   * @returns any Created SCIM token (public fields only).
   * @throws ApiError
   */
  public static createScimToken(idpName: string): CancelablePromise<{
    id: string;
    org_id: string;
    idp_name: string;
    /**
     * Unix timestamp (UTC)
     */
    created: number;
    /**
     * The SCIM bearer token
     */
    scim_token: string;
  }> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/v1/scim/tokens/{idp_name}',
      path: {
        idp_name: idpName,
      },
      errors: {
        400: `Bad request (e.g., no active organization context).`,
        401: `Unauthorized.`,
        403: `Forbidden.`,
        409: `Token already exists for this IdP.`,
        500: `Internal error (e.g., token generation failed).`,
      },
    });
  }

  /**
   * @param idpName Identity provider name
   * @returns any Deleted SCIM token (last known public representation, public fields only).
   * @throws ApiError
   */
  public static deleteScimToken(idpName: string): CancelablePromise<{
    id: string;
    org_id: string;
    idp_name: string;
    /**
     * Unix timestamp (UTC)
     */
    created: number;
    /**
     * The SCIM bearer token. Full value typically returned only on creation/rotation; may be masked or null otherwise.
     *
     */
    scim_token?: string | null;
  }> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/v1/scim/tokens/{idp_name}',
      path: {
        idp_name: idpName,
      },
      errors: {
        400: `Bad request (e.g., no active organization context).`,
        401: `Unauthorized.`,
        403: `Forbidden.`,
        404: `SCIM token not found.`,
      },
    });
  }

  /**
   * @param idpName Identity provider name
   * @returns any SCIM token for the given provider (public fields only).
   * @throws ApiError
   */
  public static getScimToken(idpName: string): CancelablePromise<{
    id: string;
    org_id: string;
    idp_name: string;
    /**
     * Unix timestamp (UTC)
     */
    created: number;
    /**
     * The SCIM bearer token. Full value typically returned only on creation/rotation; may be masked or null otherwise.
     *
     */
    scim_token?: string | null;
  }> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/scim/tokens/{idp_name}',
      path: {
        idp_name: idpName,
      },
      errors: {
        400: `Bad request (e.g., missing organization context).`,
        401: `Unauthorized.`,
        403: `Forbidden.`,
        404: `Organization or IdP not found.`,
      },
    });
  }

  /**
   * @returns any Map of IdP name to public SCIM token object and base URL.
   * @throws ApiError
   */
  public static listScimTokens(): CancelablePromise<
    Record<
      string,
      {
        /**
         * Public representation of a SCIM token (value may be masked or omitted).
         */
        token?: {
          id: string;
          org_id: string;
          idp_name: string;
          /**
           * Unix timestamp (UTC)
           */
          created: number;
          /**
           * The SCIM bearer token. Full value typically returned only on creation/rotation; may be masked or null otherwise.
           *
           */
          scim_token?: string | null;
        };
        base_url?: string;
      }
    >
  > {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/scim/tokens',
      errors: {
        400: `Bad request (e.g., missing organization context).`,
        401: `Unauthorized.`,
        403: `Forbidden.`,
      },
    });
  }

  /**
   * @param idpName Identity provider name
   * @returns any Rotated SCIM token (public fields only).
   * @throws ApiError
   */
  public static rotateScimToken(idpName: string): CancelablePromise<{
    id: string;
    org_id: string;
    idp_name: string;
    /**
     * Unix timestamp (UTC)
     */
    created: number;
    /**
     * The SCIM bearer token. Full value is returned on rotation; may be masked or null in other endpoints.
     *
     */
    scim_token: string;
  }> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/v1/scim/tokens/{idp_name}/rotate',
      path: {
        idp_name: idpName,
      },
      errors: {
        400: `Bad request (e.g., missing organization context).`,
        401: `Unauthorized.`,
        403: `Forbidden.`,
        404: `Organization or IdP not found.`,
      },
    });
  }
}
